import { ControlConfig } from '../types/config.types';
import { BoundingRect, Coordinates, Dimensions } from '../types/general.types';
import { compareObjects } from '../helpers/object_utils';
import Polygon from './Polygon';
import {
  formatFractionAsAngle,
  formatFractionAsPercent,
} from '../helpers/string_utils';
import Shape from './Shape';
import { getBoundingRectAspectRatio, getCenter } from '../helpers/size_utils';
import NailsSetter from '../infra/nails/NailsSetter';
import { ShapeConfig } from './Shape';
import Controller from '../infra/Controller';

export type StarShapeConfig = ShapeConfig & {
  sideNails: number;
  sides: number;
  maxCurveSize?: number;
  centerRadius?: number;
  rotation?: number;
  center?: Coordinates;
  size?: Dimensions;
  radius?: number;
  sidesCenterRadiusShift?: number[];
};

export default class StarShape extends Shape {
  config: StarShapeConfig;
  center: Coordinates;
  calc: ReturnType<typeof StarShape.getCalc>;

  constructor(config: StarShapeConfig) {
    super(config);

    this.setConfig(config);
  }

  static getCalc({
    radius,
    sides,
    sideNails,
    rotation,
    centerRadius: centerRadiusFraction = 0,
    maxCurveSize = 1,
    sidesCenterRadiusShift,
  }: StarShapeConfig) {
    const centerRadius = radius * centerRadiusFraction;
    const nailSpacing = (radius - centerRadius) / (sideNails - 1); // The distance between nails on the same side, in px
    const sidesAngle = (Math.PI * 2) / sides; // The angle, in radians, between each side
    const rotationAngle = rotation ? (-Math.PI * 2 * rotation) / sides : 0;

    return {
      sideAngle: sidesAngle,
      nailSpacing,
      centerRadius,
      linesPerRound: sides % 2 ? sides * 2 : sides,
      sidesConnectionCount: Math.floor(Math.min(1, maxCurveSize) * sideNails),
      sideSize: radius - centerRadius,
      sides: new Array(sides).fill(null).map((_, side) => {
        const sideAngle = side * sidesAngle + rotationAngle;

        return {
          sinSideAngle: Math.sin(sideAngle),
          cosSideAngle: Math.cos(sideAngle),
        };
      }),
      sidesCenterRadius: sidesCenterRadiusShift
        ? sidesCenterRadiusShift.map(v => centerRadius * v)
        : null,
      hasCenterNail: !centerRadius,
      sidesNailCount: centerRadius ? sideNails : sideNails - 1,
    };
  }

  getPoint(index: number): Coordinates {
    const position = index / this.config.sideNails;
    const side = Math.floor(position);
    const sideIndex = index % this.config.sideNails;

    return this.getSidePoint(side, sideIndex);
  }

  getSidePoint(side = 0, index = 0): Coordinates {
    const radius =
      this.calc.centerRadius +
      index * this.calc.nailSpacing -
      (this.calc.sidesCenterRadius?.[side] ?? 0);
    const { sinSideAngle, cosSideAngle } = this.calc.sides[side];

    return [
      this.center[0] + sinSideAngle * radius,
      this.center[1] + cosSideAngle * radius,
    ];
  }

  getSideNailKey(side: number, index: number): number {
    if (index === 0 && this.calc.hasCenterNail) {
      return this.config.getUniqueKey?.(0) ?? 0;
    }

    const nailIndex = side * this.calc.sidesNailCount + index;
    return this.config.getUniqueKey?.(nailIndex) ?? nailIndex;
  }

  setConfig(config: StarShapeConfig) {
    if (
      compareObjects(config, this.config, [
        'radius',
        'sides',
        'sideNails',
        'rotation',
        'center',
        'size',
        'centerRadius',
        'maxCurveSize',
      ])
    ) {
      return;
    }

    if (!config.center && !config.size) {
      throw new Error(
        'StarShape requires a config value for either center or size.'
      );
    }

    const center = config.center ?? getCenter(config.size);

    this.config = config;
    this.center = center;
    this.calc = StarShape.getCalc(config);
  }

  /**
   * Given a Nails instance, uses it to draw the nails of this Circle
   */
  addNails(
    nails: NailsSetter,
    {
      reverseOrder,
    }: // excludeSides,
    Partial<{
      reverseOrder?: boolean;
    }> = {}
  ): void {
    const { sides, sideNails } = this.config;
    let nailIndex = 0;
    if (this.calc.hasCenterNail) {
      nails.addNail(
        this.getUniqueKey?.(nailIndex) ?? nailIndex,
        this.getSidePoint(0, 0)
      );
      nailIndex++;
    }

    for (let side = 0; side < sides; side++) {
      for (let i = this.calc.hasCenterNail ? 1 : 0; i < sideNails; i++) {
        const sideIndex = reverseOrder ? sideNails - i : i;
        nails.addNail(
          this.getUniqueKey?.(nailIndex) ?? nailIndex,
          this.getSidePoint(side, sideIndex)
        );
        nailIndex++;
      }
    }
  }

  getNailCount(): number {
    const { sides, sideNails, centerRadius } = this.config;
    const renderCenterNail = centerRadius == null || centerRadius === 0;

    return sides * sideNails - (renderCenterNail ? sides - 1 : 0);
  }

  // In this pattern, strings are connected in a "merry-go-round" way, around the star.
  // With even sides count, the strings go around the star once, while with odd sides count, each round goes twice around the star.
  // The threading is: star at the center (or centerRadius, if > 0), then next side at the edge (outtermost nail) or the size param which represents the count of nails to use,
  // then back to the center for the next side,   // until all sides have been connected both center and edge (for odd-side-count stars) or until all sides have been
  // connected (for odd-side-count), then move up one nail from the center and start another round.
  *drawStar(
    controller: Controller,
    { size }: { size?: number } = {}
  ): Generator<void> {
    const { sideNails: sideNailsConfig, sides } = this.config;
    const { sidesConnectionCount, linesPerRound } = this.calc;

    const sideNails = size
      ? Math.max(1, Math.min(Math.floor(size), sideNailsConfig))
      : sideNailsConfig;

    const minNailIndex = Math.max(0, sideNails - sidesConnectionCount);

    let alternate = false;

    const rounds =
      sides % 2
        ? Math.ceil(Math.min(sideNails, sidesConnectionCount) / 2)
        : sideNails - minNailIndex;

    let prevPointIndex = minNailIndex;
    controller.goto(this.getSideNailKey(0, minNailIndex));

    for (let round = 0; round < rounds; round++) {
      const isLastRound = round === rounds - 1;
      let side = 0;

      for (let i = 0; i < linesPerRound; i++) {
        side = (side + 1) % sides;
        alternate = !alternate;
        prevPointIndex = alternate
          ? sideNails - round - 1
          : round + minNailIndex;
        yield controller.stringTo(this.getSideNailKey(side, prevPointIndex));

        if (isLastRound && i === sides - 1 && sides % 2 && sideNails % 2) {
          break;
        }
      }

      if (!isLastRound) {
        prevPointIndex = alternate ? prevPointIndex - 1 : prevPointIndex + 1;
        yield controller.stringTo(this.getSideNailKey(0, prevPointIndex));
      }
    }
  }

  getStepCount(size?: number) {
    return StarShape.getStepCount(this.config, { size });
  }

  static getStepCount(
    {
      sides,
      sideNails: sideNailsConfig,
      maxCurveSize = 1,
    }: Partial<StarShapeConfig>,
    { size }: { size?: number } = {}
  ) {
    const sidesConnectionCount = Math.floor(
      Math.min(1, maxCurveSize) * sideNailsConfig
    );
    const sideNails = size
      ? Math.min(Math.floor(size), sideNailsConfig)
      : sideNailsConfig;
    const minNailIndex = Math.max(0, sideNails - sidesConnectionCount);

    const rounds =
      sides % 2
        ? Math.ceil(Math.min(sideNails, sidesConnectionCount) / 2)
        : sideNails - minNailIndex;

    const linesPerRound = sides % 2 ? sides * 2 : sides;
    const isOdd = sides % 2 && sideNails % 2;
    return rounds * (linesPerRound + 1) - (isOdd ? sides : 0) - 1;
  }

  getBoundingRect(): BoundingRect {
    const { radius, sides, rotation, sideNails } = this.config;
    const polygon = new Polygon({
      size: [radius * 2, radius * 2],
      sides,
      nailsPerSide: sideNails,
      margin: 0,
      rotation,
    });

    return polygon.getBoundingRect();
  }

  getAspectRatio(): number {
    return getBoundingRectAspectRatio(this.getBoundingRect());
  }

  static nailsConfig: Readonly<ControlConfig<StarShapeConfig>> = {
    key: 'sideNails',
    label: 'Nails per side',
    defaultValue: 40,
    type: 'range',
    attr: { min: 1, max: 200, step: 1 },
    isStructural: true,
  };

  static sidesConfig: Readonly<ControlConfig<StarShapeConfig>> = {
    key: 'sides',
    label: 'Sides',
    defaultValue: 3,
    type: 'range',
    attr: { min: 3, max: 40, step: 1 },
    isStructural: true,
  };

  static maxCurveSize: Readonly<ControlConfig<StarShapeConfig>> = {
    key: 'maxCurveSize',
    label: 'Max curve size',
    description:
      'The maximum number of connections used to create a curve between two sides.',
    defaultValue: 1,
    type: 'range',
    displayValue: ({ maxCurveSize, sideNails }) =>
      Math.floor(maxCurveSize * sideNails),
    attr: {
      min: 0,
      max: 1,
      step: ({ sideNails }) => 1 / sideNails,
    },
    isStructural: true,
  };

  static centerRadiusConfig = {
    key: 'centerRadius',
    label: 'Center radius',
    defaultValue: 0,
    type: 'range',
    attr: {
      min: 0,
      max: ({ sideNails }) => (sideNails - 1) / sideNails,
      step: 0.01,
    },
    displayValue: ({ centerRadius }) => formatFractionAsPercent(centerRadius),
    isStructural: true,
  };

  static rotationConfig: Readonly<ControlConfig<StarShapeConfig>> = {
    key: 'rotation',
    label: 'Rotation',
    defaultValue: 0,
    type: 'range',
    attr: {
      min: 0,
      max: 1,
      step: 0.01,
      snap: '0.5',
    },
    displayValue: ({ rotation, sides }) =>
      formatFractionAsAngle(rotation / sides),
    isStructural: true,
    affectsStepCount: false,
  };

  static StarConfig = [
    StarShape.nailsConfig,
    StarShape.sidesConfig,
    StarShape.centerRadiusConfig,
    StarShape.maxCurveSize,
    StarShape.rotationConfig,
  ];
}
